import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Engine, Render, Runner, World, Bodies, Events } from 'matter-js';
import SplitType from 'split-type';

export default class Block07 {
    constructor (query) {
        const el = document.querySelector(query);
        if (!el) return;

        this.el = el;
        this.gsap = gsap;

        this.init();
    }

    init () {
        this.gsap.registerPlugin(ScrollTrigger);

        if (window.innerWidth > 767) {
            this.animation();
        } else {
            this.mobile();
        }
    }

    downfall () {
        const list = this.el.querySelector('.block-07--list');
        if (!list) return;

        const items = list.querySelectorAll('.block-07--list-item');
        if (!items.length) return;

        const width = list.clientWidth;
        const height = list.clientHeight;

        const engine = Engine.create();
        const { world } = engine;

        const render = Render.create({
            element: list,
            engine,
            options: {
                width,
                height,
                wireframes: false,
                background: 'transparent'
            }
        });

        Render.run(render);
        const runner = Runner.create();
        Runner.run(runner, engine);

        const thickness = 100;
        const walls = [
            Bodies.rectangle(width / 2, height + thickness / 2, width, thickness, { isStatic: true }),
            Bodies.rectangle(-thickness / 2, height / 2, thickness, height, { isStatic: true }),
            Bodies.rectangle(width + thickness / 2, height / 2, thickness, height, { isStatic: true })
        ];

        World.add(world, walls);

        let initialPositions = [
            { x: 140, y: 15 }, // Dubai
            { x: 320, y: 40 }, // Lisbon
            { x: 590, y: 0 },  // Riyadh
            { x: 620, y: 40 }, // Join Us
            { x: 360, y: 0 },  // TikTok
            { x: 240, y: 10 }, // Vimeo
            { x: 330, y: 11 }, // LinkedIn
            { x: 510, y: 30 }, // Instagram
            { x: 515, y: 44 }, // WhatsApp
            { x: 160, y: 40 }  // Discord
        ];

        if (window.innerWidth < 768) {
            initialPositions = [
                { x: 10, y: 15 }, // Dubai
                { x: 25, y: 40 }, // Lisbon
                { x: 35, y: 0 },  // Riyadh
                { x: 50, y: 40 }, // Join Us
                { x: 45, y: 0 },  // TikTok
                { x: 35, y: 10 }, // Vimeo
                { x: 50, y: 11 }, // LinkedIn
                { x: 55, y: 30 }, // Instagram
                { x: 35, y: 44 }, // WhatsApp
                { x: 45, y: 40 }  // Discord
            ];
        }

        items.forEach((item, index) => {
            const rect = item.getBoundingClientRect();
            const itemWidth = rect.width;
            const itemHeight = rect.height;

            const { x: initialX, y: initialY } = initialPositions[index];

            let body;

            const isNetwork = item.classList.contains('network');
            const isSmooth = item.classList.contains('smooth_starburst');
            const isCircle = item.classList.contains('spiky_starburst') || item.classList.contains('circle');
            const isSquare = item.classList.contains('square');

            const options = {
                restitution: 0.2,
                friction: 0.2,
                render: {
                    fillStyle: 'transparent',
                    strokeStyle: 'transparent',
                    lineWidth: 0
                }
            };

            if (isSmooth) body = Bodies.circle(initialX, initialY, itemWidth / 1.85 + 5, options);

            if (isCircle) body = Bodies.circle(initialX, initialY, itemWidth / 2 + 5, options);

            if (isSquare) {
                options.chamfer = { radius: [20, 20, 20, 20] }

                body = Bodies.rectangle(initialX, initialY, itemWidth + 10, itemHeight + 10, options);
            }

            if (isNetwork) {
                options.chamfer = { radius: [30, 30, 30, 30] }

                body = Bodies.rectangle(initialX, initialY, itemWidth + 10, itemHeight + 10, options);
            }

            World.add(world, body);

            Events.on(engine, 'afterUpdate', () => {
                item.style.transform = `translate(-50%, -50%) translate(${body.position.x}px, ${body.position.y}px) rotate(${body.angle}rad)`;
            });
        });
    }

    animation () {
        const title = this.el.querySelector('.site-section--heading .site-title');
        const text = this.el.querySelector('.site-section--heading .site-text');
        const list = this.el.querySelector('.block-07--list');

        new SplitType(title, { types: 'chars' });

        /* Step: Content */

        {
            const timeline = this.gsap.timeline({
                scrollTrigger: {
                    trigger: this.el,
                    start: () => 'top bottom',
                    end: () => `+=${window.innerHeight}px`,
                    scrub: true
                }
            });

            timeline
                .set(text,     { opacity: 0, y: '50%' })
                .to({},        { duration: 0, onUpdate: () => title.classList.remove('animated') })
                .to({},        { duration: 1 })
                .to({},        { duration: 1, onUpdate: () => title.classList.add('animated') })
                .to(text,      { duration: 1, opacity: 1, y: 0 })
                .to({},        { duration: 2 })
        }

        /* Step: Downfall */

        let start = 'top-=200 top';

        if (window.innerWidth < 1200) start = 'top bottom';

        this.gsap.timeline({
            scrollTrigger: {
                trigger: this.el,
                start: () => start,
                once: true,
                onEnter: () => {
                    list.classList.add('animated');
                    this.downfall();
                },
                scrub: true
            }
        });
    }

    mobile () {
        const title = this.el.querySelector('.site-section--heading .site-title');
        const text = this.el.querySelector('.site-section--heading .site-text');
        const list = this.el.querySelector('.block-07--list');
        
        title.classList.add('site-animation', 'fade-up');
        text.classList.add('site-animation', 'fade-up');
        list.classList.add('site-animation', 'fade-in');

        [title, text, list].forEach((element) => {
            gsap.timeline({
                scrollTrigger: {
                    trigger: element,
                    start: 'top bottom',
                    once: true,
                    onEnter: () => element.classList.add('animated')
                }
            });
        });

        /* Step: Downfall */

        this.gsap.timeline({
            scrollTrigger: {
                trigger: this.el,
                start: () => 'top bottom',
                once: true,
                onEnter: () => this.downfall(),
                scrub: true
            }
        });
    }
}